import {
	type ReactNode,
	type RefCallback,
	type RefObject,
	useCallback,
	useLayoutEffect,
	useRef,
} from 'react';

import { useMenuItemDragAndDrop } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/use-menu-item-drag-and-drop';

import { useElementRegistry } from '../../controllers/element-registry';
import type { ItemDragAndDropGroup, NavItemConfigWithCustomizationOptions } from '../../types';
import {
	type GetItemData,
	type IsItemData,
	type IsItemDataInGroup,
	type IsItemDraggable,
	type IsItemDroppable,
	type ItemData,
} from '../dnd-data';
import { getDragPreviewContentForItem, getElemBeforeForItem } from '../get-drag-preview';
import type { AnalyticsEventHandler } from '../use-item-drop';

interface ItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragAndDropEnabled?: boolean;
	isDragging: boolean;
	isItemDraggable: boolean;
	isItemDroppable: boolean;
	maybeDragPreview: ReactNode;
}

export function useItemDragAndDrop<T extends ItemData>({
	data,
	item,
	id,
	isExpandable,
	isItemDraggable,
	isItemDroppable,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	isDragAndDropEnabled,
	group,
	isItemDataInGroup,
	isItemData,
	getItemData,
}: {
	data: T;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	getItemData: GetItemData<T>;
	group: ItemDragAndDropGroup;
	id: string;
	isDragAndDropEnabled?: boolean;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	isItemData: IsItemData<T>;
	isItemDataInGroup: IsItemDataInGroup<T>;
	isItemDraggable?: IsItemDraggable<T>;
	isItemDroppable?: IsItemDroppable<T>;
	item?: NavItemConfigWithCustomizationOptions;
	onExpandFlyoutToggle: (open: boolean) => void;
}): ItemDragAndDrop {
	const itemIsDraggable = isItemDraggable?.(data) ?? !!isDragAndDropEnabled;
	const itemIsDroppable = isItemDroppable?.(data) ?? !!isDragAndDropEnabled;

	const {
		draggableAnchorRef,
		draggableButtonRef,
		state: dragState,
		dragPreview,
		dropTargetRef,
		dropIndicator,
	} = useMenuItemDragAndDrop({
		...(isDragAndDropEnabled && {
			...(itemIsDraggable
				? {
						draggable: {
							getInitialData: () => getItemData({ ...data, menuItemId: id, group } as T),
							getDragPreviewPieces: () => ({
								elemBefore: getElemBeforeForItem(item),
								content: getDragPreviewContentForItem(item, data?.label),
							}),
							canDrag: () => {
								return isItemDraggable ? isItemDraggable(data) : !!isDragAndDropEnabled;
							},
						},
					}
				: {}),
			...(itemIsDroppable
				? {
						dropTarget: {
							getData: (args) => {
								const { source } = args;
								return getItemData({
									...data,
									menuItemId: id,
									group: source?.data?.group as ItemDragAndDropGroup,
								} as T);
							},
							getOperations: () => ({
								'reorder-before': itemIsDraggable ? 'available' : undefined,
								'reorder-after': 'available',
							}),
							canDrop: (args) => {
								const { source } = args;

								const canDrop =
									isItemData(source.data) &&
									source.data.menuItemId !== id &&
									isItemDataInGroup(source.data, group);

								return canDrop;
							},
						},
					}
				: {}),
		}),
	});

	const registry = useElementRegistry();
	const registrationRef = useRef<(() => void) | null>(null);
	const draggableRef = useCallback(
		(element: HTMLAnchorElement | HTMLButtonElement | null) => {
			if (!element) {
				registrationRef.current?.();
				registrationRef.current = null;
				draggableAnchorRef.current = null;
				draggableButtonRef.current = null;
				return;
			}
			if (element instanceof HTMLAnchorElement) {
				draggableAnchorRef.current = element;
			} else {
				draggableButtonRef.current = element;
			}
			if (registrationRef.current) {
				registrationRef.current();
			}
			registrationRef.current = registry.registerL1Item({
				id,
				element,
			});
		},
		[id, registry, draggableAnchorRef, draggableButtonRef, registrationRef],
	);

	const isDragging = !!itemIsDraggable && dragState.type === 'dragging';
	const expandableClosedForDrag = useRef(false);

	useLayoutEffect(() => {
		if (!isExpandable || !itemIsDraggable) {
			return;
		}
		if (isDragging && isExpandOrFlyoutOpen) {
			onExpandFlyoutToggle(false);
			expandableClosedForDrag.current = true;
		} else if (!isDragging && expandableClosedForDrag.current) {
			onExpandFlyoutToggle(true);
			expandableClosedForDrag.current = false;
		}
	}, [
		isDragging,
		isExpandable,
		isExpandOrFlyoutOpen,
		itemIsDraggable,
		expandableClosedForDrag,
		onExpandFlyoutToggle,
	]);

	return {
		draggableRef: itemIsDraggable ? draggableRef : undefined,
		isItemDraggable: itemIsDraggable,
		isItemDroppable: itemIsDroppable,
		isDragging,
		dropTargetRef: itemIsDroppable ? dropTargetRef : undefined,
		dropIndicator: itemIsDroppable ? dropIndicator : undefined,
		maybeDragPreview: itemIsDraggable ? dragPreview : null,
	};
}
